import { Component, Inject, Optional } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef} from '@angular/material/legacy-dialog';
import { filter } from 'rxjs/operators';
import { IncidentConfirmationService } from '../../driver/services/incident-confirmation/incident-confirmation.service';
import { DurationType, IDurationType } from '../../foundation/models/enums/duration-type.enum';
import { IncidentGroupType } from '../../incident/models/enums/incident-group-type.enum';
import { MvdModalService } from '../../shared/services/modal/mvd-modal.service';
import { CustomerIndividualMedicalAlertType } from '../models/customer-individual-medical-alert-type.model';
import { CustomerIndividualMedicalCategoryType } from '../models/customer-individual-medical-category-type.model';
import { CustomerIndividualMedicalCondition } from '../models/customer-individual-medical-condition.model';
import { CustomerIndividualMedicalAlertType as CustomerIndividualMedicalAlertTypeEnum } from '../models/enums/customer-individual-medical-alert-type.enum';
import { CustomerIndividualMedicalCategoryType as CustomerIndividualMedicalCategoryTypeEnum } from '../models/enums/customer-individual-medical-category-type.enum';
import { CustomerManageService } from '../shared/customer-manage.service';
import { CustomerModalBaseComponent } from '../shared/customer-modal-base.component';

export class MedicalConditionContext {
    constructor(
        public customerNumberId: number,
        public conditionCategoryTypeId: number,
        public allowReview: boolean,
        public customerMedicalConditionId?: number,
        public businessTransactionNumberId?: number
    ) {}
}

@Component({
    selector: 'customer-condition-modal',
    templateUrl: './customer-condition-modal.html'
})
export class MedicalConditionModalComponent extends CustomerModalBaseComponent {
    public form: UntypedFormGroup;
    public showButtonLoading = false;
    public durationTypes: IDurationType[] = [];
    public context: MedicalConditionContext;
    public medicalAlertCategoryTypes: CustomerIndividualMedicalCategoryType[] = [];
    public medicalConditionTypeOptions: CustomerIndividualMedicalAlertType[] = [];

    private medicalConditionTypes: CustomerIndividualMedicalAlertType[] = [];
    private durationTypeEnum: DurationType = new DurationType();
    private allMedicalAlertTypes = new CustomerIndividualMedicalAlertTypeEnum();
    private allMedicalCategoryTypes = new CustomerIndividualMedicalCategoryTypeEnum();

    constructor(
        public formBuilder: UntypedFormBuilder,
        public service: CustomerManageService,
        public dialogRef: MatDialogRef<MedicalConditionModalComponent>,
        @Optional()
        @Inject(MAT_DIALOG_DATA)
        public data: MedicalConditionContext,
        public modalService: MvdModalService,
        private incidentConfirmationService: IncidentConfirmationService
    ) {
        super(modalService);
        this.displayResults = false;
        this.durationTypes = this.durationTypeEnum.GetActive();
        this.form = formBuilder.group({});
        let categoryControl = this.addFormControl('medicalAlertCategoryId', null, Validators.required);
        let typeControl = this.addFormControl('medicalAlertTypeId', null, Validators.required);
        let monitoringControl = this.addFormControl('isMvdMonitoring', null);
        this.addFormControl('occurrenceDate', null, null, true);
        this.addFormControl('lastEpisodeDate', null, null, true);
        this.addFormControl('reviewDurationTypeId', null);
        this.addFormControl('reviewDurationValue', null);
        this.addFormControl('isAdaptiveEquipment', null, null, true);
        categoryControl.valueChanges.subscribe(e => this.onMedicalAlertCategoryChange(e));
        typeControl.valueChanges.subscribe(e => this.onMedicalAlertTypeChange(e));
        monitoringControl.valueChanges.subscribe(e => this.onMvdMonitoringChange(e));
    }

    public ngOnInit() {
        this.service.getMedicalAlertLookups()
            .subscribe(r => {
                this.medicalAlertCategoryTypes = r.customerIndividualMedicalCategoryTypes;
                this.medicalConditionTypes = r.medicalConditionTypes;
                let categoryControl = this.form.get('medicalAlertCategoryId');

                if (null != this.data.conditionCategoryTypeId) {
                    categoryControl.setValue(this.data.conditionCategoryTypeId);
                    categoryControl.disable();
                }

                if (null != this.data.customerMedicalConditionId) {
                    this.loadMedicalCondition(this.data.customerMedicalConditionId);
                }
            });
    }

    public cancel = (): void => {
        this.dialogRef.close();
    };

    public delete = (): void => {
        this.modalService.confirm()
            .title('Confirm')
            .body('Are you sure you want to delete this condition?')
            .okBtn('Yes')
            .cancelBtn('Cancel')
            .open()
            .afterClosed()
            .pipe(filter(r => !!r))
            .subscribe(() => {
                this.service.deleteCustomerMedicalCondition(this.data.customerMedicalConditionId)
                    .subscribe(
                        () => this.dialogRef.close(true),
                        e => this.handleValidationResult(e));
            });
    };

    private onMvdMonitoringChange = (status: boolean): void => {

        if (status == true) {
            this.form.get('reviewDurationTypeId').setValidators([Validators.required]);
            this.form.get('reviewDurationValue').setValidators([Validators.min(1)]);
        } else {
            this.form.get('reviewDurationTypeId').clearValidators();
            this.form.get('reviewDurationValue').clearValidators();
        }
        this.form.get('reviewDurationTypeId').updateValueAndValidity();
        this.form.get('reviewDurationValue').updateValueAndValidity();
    };

    private onMedicalAlertCategoryChange = (medicalAlertCategoryId: number): void => {
        this.form.get('medicalAlertTypeId').setValue(null);
        this.onMedicalAlertTypeChange(null);

        if ((null == medicalAlertCategoryId) || (null == this.medicalConditionTypes)) {
            this.medicalConditionTypeOptions = [];
        } else {
            this.medicalConditionTypeOptions = this.medicalConditionTypes.filter(i => i.customerIndividualMedicalCategoryTypeId == medicalAlertCategoryId);
        }
    };

    private onMedicalAlertTypeChange = (medicalAlertTypeId: number): void => {
        let medicalAlertType = this.medicalConditionTypes.find(e => e.id == medicalAlertTypeId);

        if (null == medicalAlertType) {
            this.setFormControlRequired('lastEpisodeDate', false);
            this.setFormControlEnabled('lastEpisodeDate', false);
            this.setFormControlRequired('occurrenceDate', false);
            this.setFormControlEnabled('occurrenceDate', false);
            this.setFormControlRequired('isAdaptiveEquipment', false);
            this.setFormControlEnabled('isAdaptiveEquipment', false);
        } else {
            this.setFormControlRequired('lastEpisodeDate', true == medicalAlertType.isRequiredLastEpisodeDate);
            this.setFormControlEnabled('lastEpisodeDate', true == medicalAlertType.isRequiredLastEpisodeDate);
            this.setFormControlRequired('occurrenceDate', true == medicalAlertType.isRequiredOccurenceDate);
            this.setFormControlEnabled('occurrenceDate', true == medicalAlertType.isRequiredOccurenceDate);
            this.setFormControlRequired('isAdaptiveEquipment', true == medicalAlertType.isAdaptiveEquipment);
            this.setFormControlEnabled('isAdaptiveEquipment', true == medicalAlertType.isAdaptiveEquipment);
        }
    };

    private addFormControl = (formControlName: string, defaultValue: any, validator?: ValidatorFn | ValidatorFn[], disabled?: boolean): UntypedFormControl => {
        let newControl = this.formBuilder.control({ value: defaultValue, disabled: disabled }, validator);
        this.form.addControl(formControlName, newControl);

        return newControl;
    };

    private setFormControlRequired = (formControlName: string, isRequired: boolean): void => {
        let formControl = this.form.get(formControlName);

        if (null != formControl) {
            if (true == isRequired) {
                formControl.setValidators([Validators.required]);
            } else {
                formControl.setValidators([]);
            }
        }
    };

    private setFormControlEnabled = (formControlName: string, isEnabled: boolean): void => {
        let formControl = this.form.get(formControlName);

        if (null != formControl) {
            if (true == isEnabled) {
                formControl.enable();
            } else {
                formControl.disable();
            }
        }
    };

    public saveMedicalConditions = (): void => {
        if (this.form.valid) {
            this.showButtonLoading = true;
            let condition = new CustomerIndividualMedicalCondition();
            condition.id = this.data.customerMedicalConditionId;
            condition.customerNumberId = this.data.customerNumberId;
            condition.customerIndividualMedicalConditionTypeId = this.form.get('medicalAlertTypeId').value;
            condition.lastEpisodeDate = this.form.get('lastEpisodeDate').value;
            condition.occurrenceDate = this.form.get('occurrenceDate').value;
            condition.isAdaptiveEquipment = 'true' == this.form.get('isAdaptiveEquipment').value;
            condition.isMVDMonitoring = true == this.form.get('isMvdMonitoring').value;
            condition.durationTypeId = this.form.get('reviewDurationTypeId').value;
            condition.durationValue = this.form.get('reviewDurationValue').value;

            let model: any = {
                businessTransactionNumberId: this.data.businessTransactionNumberId,
                medicalConditions: [condition]
            };

            this.service.saveCustomerMedicalConditions(model).subscribe(
                _ => this.dialogRef.close(true),
                e => {
                    const medicalAlertType = this.allMedicalAlertTypes.FromId(condition.customerIndividualMedicalConditionTypeId);
                    let incidentGroupTypeId: number = null;

                    // This logic ensures the incident redirect we show is related to the current medical condition type that was saved
                    // in the case where the customer has multiple redirects (US 124304)
                    switch (medicalAlertType.customerIndividualMedicalCategoryType) {
                        case this.allMedicalCategoryTypes.MedicalCondition.id:
                            incidentGroupTypeId = IncidentGroupType.MedicalReportRequired;
                            break;
                        case this.allMedicalCategoryTypes.MedicalVision.id:
                            incidentGroupTypeId = IncidentGroupType.VisionReportRequired;
                            break;
                        case this.allMedicalCategoryTypes.SubstanceAbuse.id:
                            incidentGroupTypeId = IncidentGroupType.SubstanceAbuseEvaluationRequired;
                            break;
                    }

                    if (incidentGroupTypeId !== null) {
                        e.error.validationResults = [
                            ...e.error.validationResults.filter(r => r.state.requestedValue == incidentGroupTypeId),
                            ...e.error.validationResults.filter(r => r.state.requestedValue != incidentGroupTypeId),
                        ];
                    }

                    this.incidentConfirmationService.confirmIncidentRedirect(this.data.businessTransactionNumberId, e.error, this.dialogRef);
                },
                () => this.showButtonLoading = false
            );
        }
    };

    private loadMedicalCondition = (customerMedicalConditionId: number): void => {
        this.service.getCustomerMedicalConditionById(customerMedicalConditionId)
            .subscribe(r => {
                let category = this.medicalConditionTypes.find(e => e.id == r.customerIndividualMedicalConditionTypeId);
                this.form.get('medicalAlertCategoryId').setValue(category.customerIndividualMedicalCategoryTypeId);
                this.form.get('medicalAlertTypeId').setValue(r.customerIndividualMedicalConditionTypeId);
                this.form.get('occurrenceDate').setValue(r.occurrenceDate);
                this.form.get('lastEpisodeDate').setValue(r.lastEpisodeDate);
                this.form.get('isMvdMonitoring').setValue(true == r.isMVDMonitoring);
                this.form.get('reviewDurationTypeId').setValue(r.durationTypeId);
                this.form.get('reviewDurationValue').setValue(r.durationValue);
                this.form.get('isAdaptiveEquipment').setValue(true == r.isAdaptiveEquipment);
            });
    };
}
